import React, { memo, useMemo } from 'react';
import CourseLevelIcon from 'src/assets/icons/CourseLevelIcon';
import OnlineIcon from 'src/assets/icons/OnlineIcon';
import ClockIcon from 'src/assets/icons/ClockIcon';
import styled, { css } from 'styled-components';
import { typography } from 'src/config/typography';
import { media } from 'src/config/breakpoints';
import { StyleProps } from 'src/lib/styleProps';
import { Course, courseLevelLabelMap } from 'src/common/models/course';
import { CourseEvent } from 'src/common/models/event';
import { layers } from 'src/config/layers';
import { Duration } from 'src/common/components/Duration';

const Icon = styled.div`
  margin-bottom: 0.6875rem;
  text-align: center;
`;

const CategoryIcon = styled.svg``;

const Info = styled.div`
  display: flex;
  justify-content: space-between;
  margin: calc(-1 * 1.25rem) 0 0 calc(-1 * 1.25rem);
  padding-top: 1.25rem;
  width: calc(100% + 1.25rem);
  z-index: ${layers.contentTop};

  ${media.w.min.px768(css`
    background: transparent;
    justify-content: flex-start;
    padding-top: 0;
    margin: calc(-1 * 3.125rem) 0 0 calc(-1 * 3.125rem);
    width: calc(100% + 3.125rem);
  `)};
`;

const Item = styled.div`
  flex: 0 1 0;
  margin: 1.25rem 0 0 1.25rem;

  ${media.w.min.px768(css`
    margin: 3.125rem 0 0 3.125rem;
  `)};
`;

const Title = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  text-align: center;
`;

export interface CourseHeaderInfoProps {
  course: Course;
  selectedCourseEvent?: CourseEvent;
}

export const CourseHeaderInfo = memo<StyleProps & CourseHeaderInfoProps>(
  ({ className, style, course, selectedCourseEvent }) => {
    const primaryCategory = useMemo(
      () => course.categories?.find(({ main }) => main) ?? course.categories?.[0],
      [course.categories],
    );

    return (
      <Info className={className} style={style}>
        {primaryCategory && (
          <Item>
            <Icon>
              <CategoryIcon as={primaryCategory.icon} size={16} />
            </Icon>
            <Title>{primaryCategory.title}</Title>
          </Item>
        )}

        {course.level && (
          <Item>
            <Icon>
              <CourseLevelIcon level={course.level} size={16} />
            </Icon>
            <Title>{courseLevelLabelMap[course.level]}</Title>
          </Item>
        )}

        <Item>
          <Icon>
            <OnlineIcon size={16} />
          </Icon>
          <Title>
            <span style={{ whiteSpace: 'nowrap' }}>On-line</span>
            <br />
            Zoom
          </Title>
        </Item>

        {selectedCourseEvent?.duration && (
          <Item>
            <Icon>
              <ClockIcon size={16} />
            </Icon>
            <Title>
              {selectedCourseEvent && <Duration duration={selectedCourseEvent.duration} />}
            </Title>
          </Item>
        )}
      </Info>
    );
  },
);
CourseHeaderInfo.displayName = 'CourseHeaderInfo';
